<template>
  <div>
    <!-- Info boxes section -->
    <div class="row g-3 mb-5">
      <div class="col-md-4">
        <div class="bg-light rounded p-3 h-100 d-flex flex-column">
          <p class="mb-1 fw-semibold text-muted">Requested payouts</p>
          <h3 class="mb-0 fs-6 lh-sm mt-auto">
            <span
              v-for="(amount, currency) in requestedPayoutsByCurrency"
              :key="currency"
              class="me-2"
              >{{ formatMoneyWithCurrency(amount, currency) }}</span
            >
          </h3>
        </div>
      </div>
      <div class="col-md-4">
        <div class="bg-light rounded p-3 h-100 d-flex flex-column">
          <p class="mb-1 fw-semibold text-muted">Pending payouts</p>
          <h3 class="mb-0 fs-6 lh-sm mt-auto">
            {{ formatMoney(totalPendingPayouts) }} €
          </h3>
        </div>
      </div>
      <div class="col-md-4">
        <div class="bg-light rounded p-3 h-100 d-flex flex-column">
          <p class="mb-1 fw-semibold text-muted">Total payouts (30 days)</p>
          <h3 class="mb-0 fs-6 lh-sm mt-auto">
            <span
              v-for="(amount, currency) in paidPayouts30DaysByCurrency"
              :key="currency"
              class="me-2"
              >{{ formatMoneyWithCurrency(amount, currency) }}</span
            >
          </h3>
        </div>
      </div>
    </div>

    <!-- Invoices section -->
    <div class="mb-5">
      <h3 class="mb-3">Invoices</h3>
      <AdminInvoicesTable
        :invoices="allInvoicesData"
        :dateFrom="invoiceDateFrom"
        :dateTo="invoiceDateTo"
        :loading="invoicesLoading"
        @invoices-changed="refreshData"
        @date-range-changed="onDateRangeChanged"
        @view:company="$emit('view:company', $event)"
      />
    </div>

    <!-- Sales transaction fees now live on the Transactions page
         (/admin/transactions) as the "Total Fees" column + Fee CSV action. -->
    <div class="mb-5 text-muted small">
      Looking for per-transaction fees? See the
      <router-link :to="{ name: 'AdminTransactionsListView' }">
        Transactions page </router-link
      >: Total Fees column and the "Download Fee CSV" action.
    </div>

    <LoadingOverlayComponent v-if="isLoading" />
  </div>
</template>

<script setup lang="ts">
import LoadingOverlayComponent from "@/components/common/LoadingOverlayComponent.vue";
import {
  formatMoney,
  formatMoneyWithCurrency,
} from "@/composables/formatUtils";
import {
  getPendingPayouts,
  sumDisplayAmountByCurrency,
} from "@/composables/money/invoiceUtils";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { InvoiceRow } from "@/money/admin/AdminInvoicesTable.vue";
import { Invoice, InvoiceItem } from "@/types";
import { DEFAULT_CURRENCY } from "@/money/currency_constants";
import { startOfDay, subMonths } from "date-fns";
import { isEmpty } from "lodash-es";
import { computed, onMounted, ref } from "vue";
import AdminInvoicesTable from "../AdminInvoicesTable.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

defineEmits<{
  "view:company": [companyId: number];
}>();

const { globalErrorHandler } = useGlobalHandlers();
const unpaidInvoicesData = ref<Invoice[]>([]);
const thirtyDayPaidInvoicesData = ref<Invoice[]>([]);
const allInvoicesData = ref<InvoiceRow[]>([]);
const pendingInvoiceItems = ref<InvoiceItem[]>([]);

// Default date range: last 3 months
const invoiceDateFrom = ref<Date>(startOfDay(subMonths(new Date(), 3)));
const invoiceDateTo = ref<Date | null>(null);
const invoicesLoading = ref(true);
const isLoading = ref(true);

// An empty card still reads "0 €", as it did before payouts could be in other currencies.
const orZeroInDefaultCurrency = (amounts: Record<string, number>) =>
  isEmpty(amounts) ? { [DEFAULT_CURRENCY]: 0 } : amounts;

const requestedPayoutsByCurrency = computed(() =>
  orZeroInDefaultCurrency(sumDisplayAmountByCurrency(unpaidInvoicesData.value))
);

const totalPendingPayouts = computed(() =>
  getPendingPayouts(pendingInvoiceItems.value).reduce(
    (sum, payout) => sum + payout.totalAmount,
    0
  )
);

const paidPayouts30DaysByCurrency = computed(() =>
  orZeroInDefaultCurrency(
    sumDisplayAmountByCurrency(thirtyDayPaidInvoicesData.value)
  )
);

const fetchUnpaidInvoices = async () => {
  try {
    const response = await goSolidApi.get("/api/admin/unpaid-invoices");
    if (response.data) {
      unpaidInvoicesData.value = response.data.invoices;
    } else {
      throw new Error("Error fetching unpaid invoices.");
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  }
};

const fetchPaidSalesInvoices = async (days: number) => {
  try {
    const response = await goSolidApi.get(
      `/api/admin/paid-sales-invoices?days=${days}`
    );
    if (response.data) {
      thirtyDayPaidInvoicesData.value = response.data.invoices;
    } else {
      throw new Error("Error fetching paid sales invoices.");
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  }
};

const fetchAllInvoices = async () => {
  invoicesLoading.value = true;
  try {
    const params = new URLSearchParams();
    params.set("date_from", invoiceDateFrom.value.toISOString());
    if (invoiceDateTo.value) {
      const endOfDay = new Date(invoiceDateTo.value);
      endOfDay.setHours(23, 59, 59, 999);
      params.set("date_to", endOfDay.toISOString());
    }
    const response = await goSolidApi.get(
      `/api/admin/invoices?${params.toString()}`
    );
    if (response.data) {
      allInvoicesData.value = response.data.invoices;
    } else {
      throw new Error("Error fetching invoices.");
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    invoicesLoading.value = false;
  }
};

const fetchAllPendingInvoiceItems = async () => {
  try {
    const response = await goSolidApi.get("/api/admin/pending-invoice-items");
    if (response.data) {
      pendingInvoiceItems.value = response.data.invoice_items;
    } else {
      throw new Error("Error fetching invoice items.");
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  }
};

const onDateRangeChanged = async (dateFrom: Date, dateTo: Date | null) => {
  invoiceDateFrom.value = dateFrom;
  invoiceDateTo.value = dateTo;
  await fetchAllInvoices();
};

const refreshData = async () => {
  await Promise.allSettled([
    fetchUnpaidInvoices(),
    fetchPaidSalesInvoices(30),
    fetchAllInvoices(),
    fetchAllPendingInvoiceItems(),
  ]);
};

onMounted(async () => {
  try {
    await refreshData();
  } finally {
    isLoading.value = false;
  }
});
</script>
